/**
 * LoginPage — Page de connexion
 *
 * Formulaire email/mot de passe avec gestion du flux 2FA (TOTP).
 * Inclut un rate limiting côté client (3 échecs → 30s de cooldown).
 * Redirige vers /dashboard après connexion réussie.
 */

import { useState, useEffect, useRef, type FormEvent } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext.js';
import { useSettings } from '../contexts/ThemeContext.js';
import { TOTPInput } from '../components/ui/TOTPInput.js';
import { colors } from '../styles/tokens.js';

const MAX_ATTEMPTS = 3;
const COOLDOWN_SECONDS = 30;

export function LoginPage() {
  const navigate = useNavigate();
  const { login, verifyTotp, isAuthenticated } = useAuth();
  const settings = useSettings();

  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState('');
  const [isLoading, setIsLoading] = useState(false);

  // État 2FA
  const [totpStep, setTotpStep] = useState(false);
  const [tempToken, setTempToken] = useState('');
  const [totpCode, setTotpCode] = useState('');

  // Rate limiting client
  const [failedAttempts, setFailedAttempts] = useState(0);
  const [cooldownRemaining, setCooldownRemaining] = useState(0);
  const cooldownRef = useRef<ReturnType<typeof setInterval> | null>(null);

  // Rediriger si déjà connecté
  useEffect(() => {
    if (isAuthenticated) navigate('/dashboard', { replace: true });
  }, [isAuthenticated, navigate]);

  // Timer du cooldown
  useEffect(() => {
    if (cooldownRemaining <= 0) {
      if (cooldownRef.current) clearInterval(cooldownRef.current);
      return;
    }

    cooldownRef.current = setInterval(() => {
      setCooldownRemaining((prev) => {
        if (prev <= 1) {
          if (cooldownRef.current) clearInterval(cooldownRef.current);
          return 0;
        }
        return prev - 1;
      });
    }, 1000);

    return () => {
      if (cooldownRef.current) clearInterval(cooldownRef.current);
    };
  }, [cooldownRemaining]);

  /** Démarre le cooldown après trop de tentatives */
  function triggerCooldown() {
    setCooldownRemaining(COOLDOWN_SECONDS);
    setFailedAttempts(0);
  }

  const isLocked = cooldownRemaining > 0;

  async function handleLogin(e: FormEvent) {
    e.preventDefault();
    if (isLocked) return;
    setError('');
    setIsLoading(true);

    try {
      const result = await login(email, password);

      if ('requiresTOTP' in result) {
        setTempToken(result.tempToken);
        setTotpStep(true);
        setFailedAttempts(0);
      } else {
        navigate('/dashboard');
      }
    } catch (err) {
      const msg = err instanceof Error ? err.message : 'Erreur de connexion';
      setError(msg);
      const newAttempts = failedAttempts + 1;
      setFailedAttempts(newAttempts);
      if (newAttempts >= MAX_ATTEMPTS) {
        triggerCooldown();
      }
    } finally {
      setIsLoading(false);
    }
  }

  async function handleTotp(e: FormEvent) {
    e.preventDefault();
    if (totpCode.length !== 6) return;
    setError('');
    setIsLoading(true);

    try {
      await verifyTotp(tempToken, totpCode);
      navigate('/dashboard');
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Code invalide');
      setTotpCode('');
    } finally {
      setIsLoading(false);
    }
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-[var(--color-bg)] px-4">
      <div className="w-full max-w-md">
        {/* En-tête */}
        <div className="text-center mb-8">
          {settings.logoUrl ? (
            <img
              src={settings.logoUrl}
              alt={settings.siteName}
              className="h-16 mx-auto mb-4 object-contain"
            />
          ) : (
            <div className="h-16 w-16 mx-auto mb-4 rounded-xl bg-primary flex items-center justify-center">
              <span className="text-2xl font-bold text-accent">A</span>
            </div>
          )}
          <h1 className="text-2xl font-heading font-bold text-accent">
            {settings.siteName}
          </h1>
          <p className="text-sm text-text-light mt-1">{settings.siteTagline}</p>
        </div>

        {/* Formulaire */}
        <div className="bg-surface rounded-xl shadow-lg p-6 border border-gray-100">
          {!totpStep ? (
            /* Étape 1 : Email + Mot de passe */
            <form onSubmit={handleLogin} className="space-y-4">
              <h2 className="text-lg font-semibold text-accent text-center mb-2">Connexion</h2>

              {error && (
                <div role="alert" className="bg-red-50 border border-red-200 rounded-lg px-4 py-3 text-sm text-red-700">
                  {error}
                </div>
              )}

              {isLocked && (
                <div role="alert" className="bg-amber-50 border border-amber-200 rounded-lg px-4 py-3 text-sm text-amber-700 text-center">
                  Trop de tentatives. Réessayez dans {cooldownRemaining}s
                </div>
              )}

              <div>
                <label htmlFor="email" className="block text-sm font-medium text-text mb-1">
                  Email
                </label>
                <div className="relative">
                  <i className="bi bi-envelope absolute left-3 top-1/2 -translate-y-1/2" style={{ fontSize: 14, color: colors.textMuted }} />
                  <input
                    id="email"
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    required
                    disabled={isLocked}
                    autoComplete="email"
                    className="w-full pl-10 pr-4 py-2.5 rounded-lg border border-gray-300
                               text-text placeholder:text-text-light
                               focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary
                               disabled:opacity-50 disabled:cursor-not-allowed"
                    placeholder="votre@email.com"
                  />
                </div>
              </div>

              <div>
                <label htmlFor="password" className="block text-sm font-medium text-text mb-1">
                  Mot de passe
                </label>
                <div className="relative">
                  <i className="bi bi-lock absolute left-3 top-1/2 -translate-y-1/2" style={{ fontSize: 14, color: colors.textMuted }} />
                  <input
                    id="password"
                    type={showPassword ? 'text' : 'password'}
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    required
                    disabled={isLocked}
                    autoComplete="current-password"
                    className="w-full pl-10 pr-10 py-2.5 rounded-lg border border-gray-300
                               text-text placeholder:text-text-light
                               focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary
                               disabled:opacity-50 disabled:cursor-not-allowed"
                    placeholder="Votre mot de passe"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-text-light
                               hover:text-text transition-colors"
                    aria-label={showPassword ? 'Masquer le mot de passe' : 'Afficher le mot de passe'}
                  >
                    <i className={showPassword ? 'bi bi-eye-slash' : 'bi bi-eye'} style={{ fontSize: 14 }} />
                  </button>
                </div>
              </div>

              <button
                type="submit"
                disabled={isLoading || isLocked}
                className="w-full py-2.5 rounded-lg bg-primary text-accent font-semibold
                           hover:bg-primary-light transition-colors
                           disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {isLoading ? 'Connexion...' : isLocked ? `Patientez (${cooldownRemaining}s)` : 'Se connecter'}
              </button>

              <div className="text-center">
                <Link
                  to="/forgot-password"
                  className="text-sm text-text-light hover:text-text transition-colors hover:underline"
                >
                  Mot de passe oublié ?
                </Link>
              </div>
            </form>
          ) : (
            /* Étape 2 : Code TOTP avec 6 champs séparés */
            <form onSubmit={handleTotp} className="space-y-5">
              <div className="text-center mb-2">
                <i className="bi bi-shield-lock" style={{ fontSize: 32, color: colors.accent, display: 'block', marginBottom: 8 }} />
                <h2 className="text-lg font-semibold text-accent">Vérification 2FA</h2>
                <p className="text-sm text-text-light mt-1">
                  Entrez le code depuis votre application d'authentification
                </p>
              </div>

              {error && (
                <div role="alert" className="bg-red-50 border border-red-200 rounded-lg px-4 py-3 text-sm text-red-700">
                  {error}
                </div>
              )}

              <div>
                <label className="block text-sm font-medium text-text mb-3 text-center">
                  Code à 6 chiffres
                </label>
                <TOTPInput
                  value={totpCode}
                  onChange={setTotpCode}
                  disabled={isLoading}
                  autoFocus
                />
                <p className="text-xs text-text-light text-center mt-2">
                  Ce code change toutes les 30 secondes
                </p>
              </div>

              <button
                type="submit"
                disabled={isLoading || totpCode.length !== 6}
                className="w-full py-2.5 rounded-lg bg-primary text-accent font-semibold
                           hover:bg-primary-light transition-colors
                           disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {isLoading ? 'Vérification...' : 'Vérifier'}
              </button>

              <button
                type="button"
                onClick={() => {
                  setTotpStep(false);
                  setTotpCode('');
                  setError('');
                }}
                className="w-full py-2 text-sm text-text-light hover:text-text transition-colors"
              >
                Retour à la connexion
              </button>
            </form>
          )}
        </div>
      </div>
    </div>
  );
}
